import { UseFormSetValue } from 'react-hook-form'

import { UploadFormService } from '@/services/uploadForm.service'

import { PLATFORMS_CODES } from '@/constants/languages'
import {
	IFetchUploadForm,
	IUploaderForm,
	TPlatformProcessingType,
	TPlatformValue
} from '@/types/uploadForm.interface'
import { toastError } from '@/utils/toastError'

export type TUploaderFormSetValue = UseFormSetValue<IUploaderForm>

export const autoFill = async ({
	taskId,
	selectedPlatform,
	sourceAutoFillLang,
	startPlatformProcessing,
	finishPlatformAutoFilled,
	setValue
}: {
	taskId: string
	selectedPlatform: TPlatformValue
	sourceAutoFillLang: TPlatformValue | ''
	startPlatformProcessing: (
		value: TPlatformValue,
		processing: TPlatformProcessingType
	) => void
	finishPlatformAutoFilled: (value: TPlatformValue) => void
	setValue: TUploaderFormSetValue
}) => {
	if (!sourceAutoFillLang || !selectedPlatform) return

	try {
		startPlatformProcessing(selectedPlatform, 'autofill')

		const form = await UploadFormService.getFormData(taskId)

		if (!form.platforms) return

		const sourcePlatform = form.platforms[sourceAutoFillLang]

		if (!sourcePlatform || !sourcePlatform.title) return

		const target_language = PLATFORMS_CODES[selectedPlatform]
		const source_language = PLATFORMS_CODES[sourceAutoFillLang]

		const translationData = {
			title: sourcePlatform.title,
			description: sourcePlatform.description,
			location: sourcePlatform.location,
			source: sourcePlatform.source,
			restrictions: sourcePlatform.restrictions
		}

		if (!target_language || !source_language) return

		const result = await UploadFormService.getAutoFill({
			target_language,
			source_language,
			data: translationData
		})

		if (!result) return

		const targetTitle = result.title?.trim()
		const targetDesc = result.description?.trim()
		const targetLocation = result.location?.trim()
		const targetCategories = sourcePlatform.categories
		const targetRegion = sourcePlatform.region
		const targetCountry = sourcePlatform.country
		const targetRights = sourcePlatform.rights
		const targetUaeContent = sourcePlatform.UAERegulatedContent
		const targetSource = result.source?.trim()
		const targetRestrictions = result.restrictions?.trim()

		const prevPlatforms = form.platforms || {}
		const prevTargetPlatform = prevPlatforms[selectedPlatform] || {}

		const nextPlatforms = {
			...prevPlatforms,
			[selectedPlatform]: {
				...prevTargetPlatform,
				title: targetTitle,
				description: targetDesc,
				location: targetLocation,
				categories: targetCategories,
				region: targetRegion,
				country: targetCountry,
				rights: targetRights,
				UAERegulatedContent: targetUaeContent,
				source: targetSource,
				restrictions: targetRestrictions
			}
		}

		const data: IFetchUploadForm = {
			is_completed: false,
			platforms_order: form.platforms_order || [],
			platforms: nextPlatforms
		}
		await UploadFormService.sendFormData(taskId, data)

		setValue('title', targetTitle, { shouldDirty: true })
		setValue('description', targetDesc, { shouldDirty: true })
		setValue('location', targetLocation, { shouldDirty: true })
		setValue('categories', targetCategories, { shouldDirty: true })
		setValue('region', targetRegion, { shouldDirty: true })
		setValue('country', targetCountry, { shouldDirty: true })
		setValue('rights', targetRights, { shouldDirty: true })
		setValue('UAERegulatedContent', targetUaeContent, { shouldDirty: true })
		setValue('source', targetSource, { shouldDirty: true })
		setValue('restrictions', targetRestrictions, { shouldDirty: true })
	} catch (error) {
		console.log(error)

		toastError(error?.response?.data?.error)
	} finally {
		finishPlatformAutoFilled(selectedPlatform)
	}
}
